// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";

import { toast } from "react-toastify";
import { Loader, Segment } from "semantic-ui-react";

import { useAuth0 } from "../../../../auth/auth0";
import { useUserAuth } from "../../../../hooks/useUserAuth";

import * as fieldActions from "../../../../redux/actions/fieldActions";

import { FieldSearchResults } from "./FieldSearchResults.tsx";

import SearchPanel from "../../../Lumber/Search/SearchPanel.tsx";
import "./styles.css";
import { generateBooleanFilter, generateStringFilter, getDefaultGrowingSeasons } from "../../../Lumber/Search/utils.ts";
import moment from "moment";

const fieldScaleOptions = [
	{ key: "small-plot", value: "small-plot", text: "Small Plot" },
	{ key: "field-scale", value: "field-scale", text: "Field-Scale" },
];

const FieldSearch = () => {
	const dispatch = useDispatch();
	const { getTokenSilently } = useAuth0();
	const userAuth = useUserAuth();

	const DATE_FORMAT = "MM/DD/YYYY";

	const [reset, setReset] = useState(false);
	//-- Data sources
	const clientId = useSelector((state) => (state.clients ? state.clients.currentId : null));

	//-- Dropdown options
	const [cropOptions, setCropOptions] = useState([]);
	const [farmOptions, setFarmOptions] = useState([]);
	const [growingSeasons, setGrowingSeasons] = useState([]);

	const [growingSeasonOptions, setGrowingSeasonOptions] = useState([]);
	const [cooperatorCompanyOptions, setCooperatorCompanyOptions] = useState([]);

	const presetTimeOptions = useMemo(
		() => [
			{
				key: "noDate",
				value: "noDate",
				text: "No Plant Date",
			},
			{
				key: "thisYear",
				value: "thisYear",
				text: "This Year",
			},
			{
				key: "thisMonth",
				value: "thisMonth",
				text: "This Month",
			},
			{
				key: "thisWeek",
				value: "thisWeek",
				text: "This Week",
			},
			{
				key: "lastYear",
				value: "lastYear",
				text: "Last Year",
			},
			{
				key: "lastMonth",
				value: "lastMonth",
				text: "Last Month",
			},
			{
				key: "lastWeek",
				value: "lastWeek",
				text: "Last Week",
			},
			{
				key: "custom",
				value: "custom",
				text: "Custom",
			},
			{
				key: "allDates",
				value: "allDates",
				text: "All Dates",
			},
		],
		[reset],
	);
	const [locationOptions, setLocationOptions] = useState({
		cities: [],
		regions: [],
		countries: [],
	});

	//-- Selected filter options
	//-- When setting the default time filter to this year or last year, update these dates to getFullYear() (this year) or getFullYear() - 1
	//-- Also update the presetTimeId to either ("thisYear") or ("lastYear")
	//-- e.g.
	const [plantDateFrom, setPlantDateFrom] = useState(new Date(new Date().getFullYear() - 1, 0, 1));
	const [plantDateTo, setPlantDateTo] = useState(new Date(new Date().getFullYear() - 1, 11, 31));

	const [presetTimeId, setPresetTimeId] = useState("allDates");
	const [searchAllClients, setSearchAllClients] = useState(false);
	const [fieldSearchTerm, setFieldSearchTerm] = useState("");
	const [showNullDates, setShowNullDates] = useState(true);
	const [filters, setFilters] = useState({});

	//-- Search results
	const [fieldSearchResults, setFieldSearchResults] = useState(null);
	const [fieldLogData, setFieldLogData] = useState(null);

	//-- UI Control
	const [loading, setLoading] = useState(true);
	const [loadingSearchResults, setLoadingSearchResults] = useState(true);

	useEffect(() => {
		if (userAuth.isReady && clientId) {
			setLoadingSearchResults(true);
			searchForFields(clientId);
			//-- DO NOT setLoadingSearchResults(false) because the loader will not show
			//-- The next useEffect will take care of setting it to false
		}
	}, [userAuth.isReady, clientId, searchAllClients]);

	useEffect(() => {
		setDropdownOptions();
	}, [fieldLogData]);

	useEffect(() => {
		if (fieldSearchResults) {
			setLoadingSearchResults(true);
			setLoadingSearchResults(false);
		}
	}, [fieldSearchResults]);

	useEffect(() => {
		if (fieldSearchResults) {
			setDropdownOptions();
		}
	}, [searchAllClients]);

	useEffect(() => {
		if (fieldSearchResults) {
			const fieldTrialsMap = fieldSearchResults.trialInfos.reduce((acc, curr) => {
				if (acc[curr.fieldId]) {
					acc[curr.fieldId] = [...acc[curr.fieldId], { trialId: curr.trialId, trialName: curr.trialName }];
				} else {
					acc[curr.fieldId] = [{ trialId: curr.trialId, trialName: curr.trialName }];
				}
				return acc;
			}, {});

			setFieldLogData(
				fieldSearchResults.fieldInfos.map((fi) => ({
					...fi,
					farm: {
						farmName: fi.farmName,
						farmAbbr: fi.farmAbbr,
					},
					coopLocation: {
						cooperatorName: fi.cooperatorName,
						region: fi.region,
						country: fi.country,
						countryCode: fi.countryCode,
						city: fi.city,
					},
					plotDimensions: {
						numOfColumns: fi.numOfColumns,
						numOfRanges: fi.numOfRanges,
					},
					plotSize: {
						plotWidth: fi.plotWidth,
						plotLength: fi.plotLength,
					},
					trials: fieldTrialsMap[fi.fieldId],
				})),
			);
		}
	}, [fieldSearchResults]);

	async function searchForFields(clientId) {
		if (clientId && plantDateTo && plantDateFrom) {
			const accessToken = await getTokenSilently();
			setLoadingSearchResults(true);
			dispatch(
				fieldActions.searchForFields(
					{
						plantDateFrom: new Date(2001, 0, 1).toDateString(DATE_FORMAT),
						plantDateTo: new Date(2099, 0, 1).toDateString(DATE_FORMAT),
					},
					clientId,
					searchAllClients,
					accessToken,
				),
			)
				.then((res) => {
					setFieldSearchResultsState(res);
					setLoading(false);
					setLoadingSearchResults(false);
				})
				.catch((err) => {
					toast.error("Error searching for fields. Please try again.");
					setLoading(false);
					setLoadingSearchResults(false);
					console.log(err);
				});
		}
	}

	function setDropdownOptions() {
		setCropOptionsState(fieldLogData);
		setFarmOptionsState(fieldLogData);
		setGrowingSeasonOptionsState(fieldLogData);
		setCooperatorCompanyOptionsState(fieldLogData);
		setLocationOptionsState(fieldLogData);
	}

	function setCropOptionsState(crops) {
		setCropOptions(
			_.chain(crops)
				.sortBy("cropName")
				.sortedUniqBy("cropName")
				.map(({ cropId, cropName }) => {
					const opt = { key: cropId, value: cropId, text: cropName };
					return opt;
				})
				.value(),
		);
	}

	function setFarmOptionsState(farms) {
		setFarmOptions(
			_.chain(farms)
				.sortBy("farmName")
				.sortedUniqBy("farmName")
				.map(({ farmId, farmName, clientName }) => {
					const opt = {
						key: farmId,
						value: farmName,
						text: farmName,
						content: (
							<div>
								<span style={{ fontSize: 14, color: "#000000" }}>{farmName}</span>
								<span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span>
							</div>
						),
					};
					return opt;
				})
				.value(),
		);
	}

	function setGrowingSeasonOptionsState(growingSeasons) {
		let tempGsOptions = _.chain(growingSeasons)
			.sortBy("growingSeasonName")
			.sortedUniqBy("growingSeasonName")
			.map(({ growingSeasonId, growingSeasonName }) => {
				const opt = { key: growingSeasonId, value: growingSeasonId, text: growingSeasonName };
				return opt;
			})
			.reverse()
			.value();
		setGrowingSeasonOptions(tempGsOptions);

		//-- Set growing season to default gs
		let defaultGrowingSeasons = getDefaultGrowingSeasons();
		let filteredSelections = _.map(
			_.filter(tempGsOptions, (gs) => defaultGrowingSeasons.includes(gs.text)),
			"value",
		);
		let defaultGrowingSeasonSelections = filteredSelections.length > 0 ? filteredSelections : [tempGsOptions[0]?.value];
		setGrowingSeasons(defaultGrowingSeasonSelections);
	}

	function setCooperatorCompanyOptionsState(companies) {
		setCooperatorCompanyOptions(
			_.chain(companies)
				.sortBy("cooperatorName")
				.sortedUniqBy("cooperatorName")
				.map(({ cooperatorCompanyId, cooperatorName }) => {
					const opt = {
						key: cooperatorCompanyId,
						value: cooperatorName,
						text: cooperatorName,
						content: (
							<div>
								<span style={{ fontSize: 14, color: "#000000" }}>{cooperatorName}</span>
								{/* <span style={{ float: "right", fontSize: 12, opacity: 0.75 }}>({clientName})</span> */}
							</div>
						),
					};
					return opt;
				})
				.value(),
		);
	}

	function setFieldSearchResultsState(searchResults) {
		if (searchResults) {
			setFieldSearchResults(searchResults);
		}
	}

	function setLocationOptionsState(locations) {
		const cityOpts = _.chain(locations)
			.sortBy("city")
			.sortedUniqBy("city")
			.map(({ city }) => {
				const opt = {
					key: city,
					value: city,
					text: city,
				};
				return opt;
			})
			.value();

		const regionOpts = _.chain(locations)
			.sortBy("region")
			.sortedUniqBy("region")
			.map(({ region }) => {
				const opt = {
					key: region,
					value: region,
					text: region,
				};
				return opt;
			})
			.value();

		const countryOpts = _.chain(locations)
			.sortBy("country")
			.sortedUniqBy("country")
			.map(({ country }) => {
				const opt = {
					key: country,
					value: country,
					text: country,
				};
				return opt;
			})
			.value();

		setLocationOptions({
			cities: cityOpts,
			regions: regionOpts,
			countries: countryOpts,
		});
	}

	useEffect(() => {
		setFilters((prev) => ({
			...prev,
			plantDate: {
				type: "date",
				startDate: moment(plantDateFrom),
				endDate: moment(plantDateTo),
				showNulls: showNullDates,
				applied: presetTimeId !== "allDates" || !showNullDates,
			},
		}));
	}, [plantDateFrom, plantDateTo, showNullDates, presetTimeId]);

	const handleFilters = {
		farm: generateStringFilter("farmName", setFilters, "farmAbbr"),
		growingSeason: generateStringFilter("growingSeasonId", setFilters, "growingSeasonName"),
		crop: generateStringFilter("cropId", setFilters, "cropName"),
		cooperatorCompany: generateStringFilter("cooperatorName", setFilters),
		cities: generateStringFilter("city", setFilters),
		regions: generateStringFilter("region", setFilters),
		countries: generateStringFilter("country", setFilters),
		fieldScale: generateBooleanFilter("fieldScaleTrial", setFilters, { "field-scale": true, "small-plot": false }),
	};

	const resetFilter = () => {
		setPlantDateFrom(new Date(2001, 0, 1));
		setPlantDateTo(new Date(2099, 0, 1));
		setPresetTimeId("allDates");
		setFieldSearchTerm("");
		setDropdownOptions();
		setReset((prev) => !prev);
		setShowNullDates(true);
		setSearchAllClients(false);
	};

	useEffect(() => {
		if (presetTimeId) {
			if (presetTimeId === "thisYear") {
				setPlantDateFrom(new Date(new Date().getFullYear(), 0, 1));
				setPlantDateTo(new Date(new Date().getFullYear(), 11, 31));
			} else if (presetTimeId === "thisMonth") {
				let lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();

				setPlantDateFrom(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
				setPlantDateTo(new Date(new Date().getFullYear(), new Date().getMonth(), lastDayOfMonth));
			} else if (presetTimeId === "thisWeek") {
				let currentDay = new Date();
				let diff = currentDay.getDate() - currentDay.getDay() + 1;
				let beginningOfWeek = new Date(new Date(currentDay).setDate(diff - 1));
				let endOfWeek = new Date(new Date(currentDay).setDate(diff + 5));

				setPlantDateFrom(beginningOfWeek);
				setPlantDateTo(endOfWeek);
			} else if (presetTimeId === "lastYear") {
				setPlantDateFrom(new Date(new Date().getFullYear() - 1, 0, 1));
				setPlantDateTo(new Date(new Date().getFullYear() - 1, 11, 31));
			} else if (presetTimeId === "lastMonth") {
				let lastDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 0).getDate();
				let year = new Date().getFullYear();
				if (new Date().getMonth() === 1) {
					year = new Date().getFullYear() - 1;
				}

				setPlantDateFrom(new Date(year, new Date().getMonth() - 1, 1));
				setPlantDateTo(new Date(year, new Date().getMonth() - 1, lastDayOfMonth));
			} else if (presetTimeId === "lastWeek") {
				let currentDay = new Date();
				let diff = currentDay.getDate() - currentDay.getDay() + 1;
				let beginningOfWeek = new Date(new Date(currentDay).setDate(diff - 8));
				let endOfWeek = new Date(new Date(currentDay).setDate(diff - 2));

				setPlantDateFrom(beginningOfWeek);
				setPlantDateTo(endOfWeek);
			} else if (presetTimeId === "allDates") {
				setPlantDateFrom(new Date(2001, 0, 1));
				setPlantDateTo(new Date(2099, 0, 1));
			} else if (presetTimeId === "noDate") {
				setPlantDateFrom(null);
				setPlantDateTo(null);
			}
		}
	}, [presetTimeId]);

	return loading ? (
		<Loader active />
	) : !loading && fieldLogData === null ? (
		<Segment style={{ width: "100%", background: "white", margin: 15 }}>
			<Segment>
				<p>{"Field Search could not be loaded. Please try again. If the problem persists, please contact support."}</p>
			</Segment>
		</Segment>
	) : (
		<Segment basic style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0" }}>
			<SearchPanel onReset={resetFilter}>
				<SearchPanel.Bar placeholder="Search Fields" searchTerm={fieldSearchTerm} setSearchTerm={setFieldSearchTerm} />
				<SearchPanel.MultiDropdown
					options={growingSeasonOptions}
					defaultOptionIds={growingSeasons}
					placeholder="Growing Season"
					setFilters={handleFilters.growingSeason}
				/>
				<SearchPanel.MultiDropdown
					options={farmOptions}
					placeholder="Farms"
					size="small"
					setFilters={handleFilters.farm}
				/>
				<SearchPanel.MultiDropdown
					options={cropOptions}
					placeholder="Crops"
					size="small"
					setFilters={handleFilters.crop}
				/>
				<SearchPanel.MultiDropdown
					options={cooperatorCompanyOptions}
					placeholder="Cooperator"
					size="small"
					setFilters={handleFilters.cooperatorCompany}
				/>
				<SearchPanel.MultiDropdown
					searchable={false}
					options={fieldScaleOptions}
					placeholder="Trial Type"
					size="small"
					setFilters={handleFilters.fieldScale}
				/>
				<SearchPanel.AdvancedPopup filters={filters} advancedHeaders={["plantDate"]}>
					<SearchPanel.Date
						label="Plant Date From"
						placeholder="From"
						date={plantDateFrom}
						setDate={setPlantDateFrom}
					/>
					<SearchPanel.Date label="Plant Date To" placeholder="To" date={plantDateTo} setDate={setPlantDateTo} />
					<SearchPanel.Dropdown
						label="Preset Date Range"
						placeholder="Date Range"
						options={presetTimeOptions ?? []}
						setSelection={setPresetTimeId}
						defaultOption={presetTimeOptions[presetTimeOptions.length - 1]} // all dates
					/>
					<SearchPanel.MultiDropdown
						placeholder="Cities"
						options={locationOptions.cities}
						setFilters={handleFilters.cities}
					/>
					<SearchPanel.MultiDropdown
						placeholder="States / Regions"
						options={locationOptions.regions}
						setFilters={handleFilters.regions}
					/>
					<SearchPanel.MultiDropdown
						placeholder="Countries"
						options={locationOptions.countries}
						setFilters={handleFilters.countries}
					/>
					<SearchPanel.Checkbox
						label="Show Results With No Plant Date"
						setChecked={setShowNullDates}
						checked={showNullDates}
					/>
					<SearchPanel.Checkbox
						label="Search All Clients"
						checked={searchAllClients}
						setChecked={setSearchAllClients}
					/>
				</SearchPanel.AdvancedPopup>
			</SearchPanel>
			<FieldSearchResults
				data={fieldLogData}
				loading={loadingSearchResults}
				filters={filters}
				searchTerm={{ term: fieldSearchTerm, cols: ["fieldName"] }}
			/>
		</Segment>
	);
};

export default FieldSearch;
